/* eslint-disable */
import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import {
  environmentName,
  tracesSampleRate,
  sampleRate,
  replaysSessionSampleRate,
  replaysOnErrorSampleRate
} from "config/constant";
import "./index.css";
import "./scoped-preflight.css";
import App from "./App";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from "react-router-dom";
import { store } from "store/store";
import { ATX_TEARDOWN_EVENT } from "switcher";
import { isActiveProctoringSession, observeProviewRoot } from "helpers/proview-root-monitor";
import { ClientErrorTypes } from "globals/enums";

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_ENVIRONMENT,
  integrations: (integrations) => {
    return integrations
      .filter((integration) => integration.name !== "BrowserApiErrors")
      .concat([
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        }),
        Sentry.replayIntegration(),
        Sentry.thirdPartyErrorFilterIntegration({
          filterKeys: [process.env.SENTRY_FILTER_KEY],
          behaviour: "apply-tag-if-exclusively-contains-third-party-frames",
        }),
      ]);
  },
  tracesSampleRate: tracesSampleRate,
  release: process.env.SENTRY_RELEASE,
  replaysSessionSampleRate: replaysSessionSampleRate,
  replaysOnErrorSampleRate: replaysOnErrorSampleRate,
  ignoreErrors: ["AbortError: The play() request was interrupted"]
});

let proviewRoot = document.getElementById("proview_root") as HTMLElement;
let chatContainer = document.getElementById("tvp-chat-container") as HTMLElement;
if (!proviewRoot) {
  proviewRoot = document.createElement("div") as HTMLElement;
  proviewRoot.id = "proview_root";
  proviewRoot.style.backgroundColor = "#000";
  proviewRoot.style.display = "flex";
  proviewRoot.style.justifyContent = "center";
  proviewRoot.style.alignItems = "center";
  proviewRoot.style.zIndex = "9999999";
  proviewRoot.style.position = "fixed";
  proviewRoot.style.top = "0px";
  proviewRoot.style.bottom = "-14px";
  proviewRoot.style.left = "0px";
  proviewRoot.style.right = "0px";
  document.body.appendChild(proviewRoot);
} else {
  proviewRoot.style.setProperty("display", "flex", "important");
}

if (!chatContainer) {
  chatContainer = document.createElement("div") as HTMLElement;
  chatContainer.id = "tvp-chat-container";
  chatContainer.className = "tvp-proview_root";
  chatContainer.style.zIndex = "999999999";
  chatContainer.style.position = "absolute";
  chatContainer.style.bottom = "0px";
  chatContainer.style.right = "0px";
  chatContainer.style.display = "block";
  document.body.appendChild(chatContainer);
}

let root: ReturnType<typeof ReactDOM.createRoot> | null = null;
let lastSessionId: number | null = null;
let expectedProviewRootRemoval = false;

const getHostControl = () =>
  window.ProctorClient3?.giveControl?.() ?? window?.tv?.q?.[0]?.[2];

const reportHostError = (error: unknown): void => {
  const control = getHostControl();

  if (typeof control?.errorCallback === "function") {
    control.errorCallback(error, ClientErrorTypes.ProviewRootRemovedError);
  }
};

observeProviewRoot({
  root: proviewRoot,
  getIsMonitoring: () => {
    const appState = store.getState().app;
    return isActiveProctoringSession({
      sessionId: appState.session.session_id,
      sessionStatus: appState.sessionStatus
    });
  },
  getIsExpectedRemoval: () => expectedProviewRootRemoval,
  onError: reportHostError
});

const mountProviewApp = () => {
  if (!root) {
    root = ReactDOM.createRoot(proviewRoot);
  }
  root.render(<App />);
};

const unmountProviewApp = () => {
  if (root) {
    root.unmount();
    root = null;
  }
};

const remountProviewApp = () => {
  unmountProviewApp();
  mountProviewApp();
};

/**
 * The ATX (v8) SDK now has a usable screen, so this client can stand down.
 *
 * This fires only once ATX is ready, never earlier: until then this client's
 * splash is still mounted and, being a full-screen overlay, is what the
 * candidate sees while ATX boots.
 *
 * The script and the store stay loaded — only the React tree stops, so its
 * `beforeunload` guard and detection hooks cannot run under the ATX session.
 * `window.ProctorClient3` is deliberately left in place for the ATX wrapper to
 * take over — hosts (and helpers/sentry.ts) read it without guarding.
 */
document.addEventListener(ATX_TEARDOWN_EVENT, () => {
  expectedProviewRootRemoval = true;
  unmountProviewApp();
  proviewRoot.remove();
  chatContainer.remove();
  // Shell mounts the recorder widget in a root of its own, which the main
  // root's unmount does not reach.
  document.getElementById("tvp_recorder_holder")?.closest(".tvp-proview_root")?.remove();
});

if (environmentName === "development") {
  const { worker } = require("./mocks/browser");
  worker.start();

  if (module.hot) {
    module.hot.accept();
  }
}

mountProviewApp();

store.subscribe(() => {
  const sessionId = store.getState()?.app?.session?.session_id;
  if (!sessionId) return;
  if (lastSessionId === sessionId) return;
  lastSessionId = sessionId;
  setTimeout(() => {
    remountProviewApp();
  }, 0);
});
